import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { ImportationService } from '../../service/ImportationService';

const importationService = new ImportationService();

const initialState = {
  testList: [],
  importationList: [],
  importationListCount: 0,
  status: 'idle',
  error: null
};

// The function below is called a thunk and allows us to perform async logic. It
// can be dispatched like a regular action: `dispatch(incrementAsync(10))`. This
// will call the thunk with the `dispatch` function as the first argument. Async
// code can then be executed and other actions can be dispatched. Thunks are
// typically used to make async requests.
export const getImportationsAsync = createAsyncThunk(
  'importationList',
  async ({page = 0, first = 0, rows = 10, sortField = 'id', sortOrder = 1, filters = {}}, { rejectWithValue }) => {
    try {
      return await importationService.getImportations(page, first, rows, sortField, sortOrder, filters);
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Erro ao buscar importações');
    }
  }
);

export const getImportationsCountAsync = createAsyncThunk(
  'importationListCount',
  async ({filters = {}}, { rejectWithValue }) => {
    try {
      return await importationService.getCountImportations(filters);
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Erro ao buscar contagem de importações');
    }
  }
);

export const getTestsAsync = createAsyncThunk(
  'testList',
  async (_, { rejectWithValue }) => {
    try {
      return await importationService.getTests();
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Erro ao buscar testes');
    }
  }
);

export const deleteImportationAsync = createAsyncThunk(
  'deleteImportation',
  async ({id, gridParameters}, { rejectWithValue }) => {
    try {
      await importationService.deleteImportation(id);
      const count = await importationService.getCountImportations(gridParameters.filters);
      const list = await importationService.getImportations(0, 0, gridParameters.rows, gridParameters.sortField, gridParameters.sortOrder, gridParameters.filters);
      return {
        count,
        list
      };
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Erro ao excluir importação');
    }
  }
);

export const deleteImportationsAsync = createAsyncThunk(
  'deleteImportations',
  async ({ids, gridParameters}, { rejectWithValue }) => {
    try {
      await importationService.deleteImportations(ids);
      const count = await importationService.getCountImportations(gridParameters.filters);
      const list = await importationService.getImportations(0, 0, gridParameters.rows, gridParameters.sortField, gridParameters.sortOrder, gridParameters.filters);
      return {
        count,
        list
      };
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Erro ao excluir importações');
    }
  }
);

export const editImportationAsync = createAsyncThunk(
  'editImportation',
  async ({importation, gridParameters}, { rejectWithValue }) => {
    try {
      await importationService.editImportation(importation);
      const count = await importationService.getCountImportations(gridParameters.filters);
      const list = await importationService.getImportations(gridParameters.page, gridParameters.first, gridParameters.rows, gridParameters.sortField, gridParameters.sortOrder, gridParameters.filters);
      return {
        count,
        list
      };
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Erro ao editar importação');
    }
  }
);

export const importationFinished = createAsyncThunk(
  'importationFinished',
  async ({gridParameters}, { rejectWithValue }) => {
    try {
      const count = await importationService.getCountImportations(gridParameters.filters);
      const list = await importationService.getImportations(gridParameters.page, gridParameters.first, gridParameters.rows, gridParameters.sortField, gridParameters.sortOrder, gridParameters.filters);
      return {
        count,
        list
      };
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Erro ao finalizar importação');
    }
  }
);

export const importationSlice = createSlice({
  name: 'importationSlice',
  initialState,
  // The `reducers` field lets us define reducers and generate associated actions
  reducers: {
    importationStarted: (state, action) => {
      state.status = 'loading';
      state.error = null;
    }
  },
  // The `extraReducers` field lets the slice handle actions defined elsewhere,
  // including actions generated by createAsyncThunk or in other slices.
  extraReducers: (builder) => {
    builder
      .addCase(getTestsAsync.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(getTestsAsync.fulfilled, (state, action) => {
        state.status = 'idle';
        state.testList = action.payload;
        state.error = null;
      })
      .addCase(getTestsAsync.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload;
      })
      .addCase(getImportationsAsync.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(getImportationsCountAsync.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(getImportationsAsync.fulfilled, (state, action) => {
        state.status = 'idle';
        state.importationList = [...action.payload.data || []].map(d => {
            d['errorList'] = [...d.errorList || []].map((it) => ({description: it}));
            d['hasErrors'] = [...d.errorList || []].length > 0;
            return d;
        });
        state.error = null;
      })
      .addCase(getImportationsAsync.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload;
      })
      .addCase(getImportationsCountAsync.fulfilled, (state, action) => {
        state.status = 'idle';
        state.importationListCount = action.payload;
        state.error = null;
      })
      .addCase(getImportationsCountAsync.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload;
      })
      .addCase(editImportationAsync.fulfilled, (state, action) => {
        state.status = 'idle';
        state.importationList = action.payload.list.data;
        state.importationListCount = action.payload.count;
        state.error = null;
      })
      .addCase(editImportationAsync.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload;
      })
      .addCase(deleteImportationAsync.fulfilled, (state, action) => {
        state.status = 'idle';
        state.importationList = action.payload.list.data;
        state.importationListCount = action.payload.count;
        state.error = null;
      })
      .addCase(deleteImportationAsync.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload;
      })
      .addCase(deleteImportationsAsync.fulfilled, (state, action) => {
        state.status = 'idle';
        state.importationList = action.payload.list.data;
        state.importationListCount = action.payload.count;
        state.error = null;
      })
      .addCase(deleteImportationsAsync.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload;
      })
      .addCase(importationFinished.fulfilled, (state, action) => {
        state.status = 'idle';
        state.importationList = action.payload.list.data;
        state.importationListCount = action.payload.count;
        state.error = null;
      })
      .addCase(importationFinished.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload;
      });
  },
});

export const { importationStarted } = importationSlice.actions;

export const selectImportationList = (state) => state.importation.importationList;

export const selectImportationListCount = (state) => state.importation.importationListCount;

export const selectLoading = (state) => state.importation.status === 'loading';

export const selectError = (state) => state.importation.error;

export default importationSlice.reducer;